import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { Family, Relation } from './useFamily';
import { CreateClientPayload } from './useCreateClient';

export type CreateFamilyPayload = {
  name: string;
  advisorUserId: string;
  mainFamilyMember?: FamilyMemberWithoutRelation;
  secondaryFamilyMembers?: FamilyMemberWithRelation[];
  members?: FamilyMemberPeer[];
  relationships?: {
    fromClientId: string;
    toClientId: string;
    relationshipType: Relation;
  }[];
};

type FamilyMemberPeer = {
  clientId: string;
  familyRoleId?: string | null;
};

export type FamilyMemberWithoutRelation =
  | {
      clientId: string;
    }
  | CreateClientPayload;

export type FamilyMemberWithRelation = FamilyMemberWithoutRelation & {
  relationshipToMainFamilyMember: Relation;
};

export function useCreateFamily() {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    error,
    isPending: isLoading,
    reset,
  } = useMutation<{ data: Family }, Error, CreateFamilyPayload>({
    mutationFn: (values: CreateFamilyPayload) => api.post<Family>('families', values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['families'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const createFamily = async (values: CreateFamilyPayload) => {
    return mutateAsync(values);
  };

  return {
    createFamily,
    error,
    isLoading,
    reset,
  };
}
